/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import { values, flatten, uniq } from "lodash";
import { Container, Button } from "semantic-ui-react";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { createValidator } from "not-valid";
import * as moment from "moment";

import { OldProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireContainer";
import { OldProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { AnsweredQuestion, AnsweredQuestionWithId } from "@teamprevent/common/model";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { submitInvalidQuestionnaire, updateQuestion, removeQuestion } from "@teamprevent/common/questionnaires/actions";
import { CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig/models";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";
import { QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";

import { loadVaccinationConsultation } from "../actions";
import { ImmunisationTaskModel } from "../../task/model";
import { employeeRecordSelector } from "../../crud/employeeRecord/selectors";
import { routeIdSelector as caseIdSelector, ohCaseSelector } from "../../crud/ohCase/selectors";
import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";
import { PartialOhCaseDetailModel } from "../../crud/ohCase";
import { DataProtectionSectionPrint } from "../../consultations/components/DataProtectionSectionPrint";
import { ConfidentialitySectionPrint } from "../../consultations/components/ConfidentialitySectionPrint";
import { ApplicationState } from "../../applicationState";
import { EmployeeRecord } from "../../crud/employeeRecord";
import { DidNotAttendButton } from "../../clinicalNotes/components/DidNotAttendButton";
import { loadOhCaseDetail } from "../../crud/ohCase/actions";
import { vaccineRoute, vaccines, vaccineSite } from "../models";

interface StateProps {
    customerId: string;
    taskId: string;
    task: ImmunisationTaskModel;
    ohCaseId: string;
    ohCase: PartialOhCaseDetailModel;
    readonly: boolean;
    questions: AnsweredQuestionWithId[];
    employeeRecord: EmployeeRecord;
}

interface DispatchProps {
    loadOhCaseDetail: (ohCaseId: string, customerId: string) => void;
    loadQuestionnaire: (ohCaseId: string, taskId: string, customerId: string) => void;
    submitInvalidQuestionnaire: () => void;
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }) => void;
    removeQuestion: (payload: { sectionId: string; id: string }) => void;
}

interface OwnProps {
    submitQuestionnaire: (questions: AnsweredQuestionWithId[], immunisationTaskTypeIds: string[]) => void;
    tasksSection: React.JSX.Element;
    tasksSectionValid: boolean;
}

type Props = StateProps & DispatchProps & OwnProps;

export const QUESTIONNAIRE_KEY = "VaccinationConsultation";

const manufacturerAndDoseQuestion = { id: "ManufacturerAndDose", questionText: "Manufacturer And Dose" };
const batchNumberQuestion = { id: "BatchNumber", questionText: "Batch Number" };
const expiryDateQuestion = { id: "ExpiryDate", questionText: "Expiry Date" };
const routeQuestion = { id: "Route", questionText: "Route" };
const siteQuestion = { id: "Site", questionText: "Site" };
const adviceLeafletProvidedQuestion = { id: "AdviceLeafletProvided", questionText: "Advice leaflet provided to the employee" };
const consentGivenQuestionId = "ConsentToGiveVaccine";
const noVaccinesAdministeredTodayQuestionId = "NoVaccineWasGivenOrRefused";
const pleaseExplainWhyNoVaccinationsWereGivenId = "PleaseExplainWhyNoVaccinationsWereGiven";

const questionsList: CustomQuestionConfig[] = [
    manufacturerAndDoseQuestion,
    batchNumberQuestion,
    expiryDateQuestion,
    routeQuestion,
    siteQuestion,
    adviceLeafletProvidedQuestion
];

export const VaccinationConsultationNotesPrintUnconnected: React.FunctionComponent<Props> = (props: Props) => {
    const [loadingImmunisationTypes, setLoadingImmunisationTypes] = React.useState(true);
    const [loadingConsultation, setLoadingConsultation] = React.useState(true);
    const [consentToGiveVaccine, setConsentToGiveVaccine] = React.useState(false);
    const [immunisationTypeTasks, setImmunisationTypeTasks] = React.useState((props.task && props.task.immunisationTypeTasks) || []);
    const [noVaccinesAdministeredToday, setNoVaccinesAdministeredToday] = React.useState(false);

    React.useEffect(() => {
        if (!props.ohCase || !props.ohCase.id) {
            props.loadOhCaseDetail(props.ohCaseId, props.customerId);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const loadConsentToGiveVaccine = () => {
            const consentQuestion = props.questions.find(q => q.id === consentGivenQuestionId);
            const consentGiven = consentQuestion && consentQuestion.answer;
            setConsentToGiveVaccine(!!consentGiven);
        };

        const loadNoVaccinesAdministeredToday = () => {
            const noVaccinesAdministeredQuestion = props.questions.find(q => q.id === noVaccinesAdministeredTodayQuestionId);
            const noVaccinesAdministered = noVaccinesAdministeredQuestion && noVaccinesAdministeredQuestion.answer;
            setNoVaccinesAdministeredToday(!!noVaccinesAdministered);
        };

        if (loadingImmunisationTypes) {
            if (props.task?.immunisationTypeTasks?.length > 0) {
                setImmunisationTypeTasks(props.task.immunisationTypeTasks);
                setLoadingImmunisationTypes(false);
            }
        }

        if (!loadingImmunisationTypes && loadingConsultation) {
            if (props.questions.length > 0) {
                loadConsentToGiveVaccine();
                loadNoVaccinesAdministeredToday();
                setLoadingConsultation(false);
            }
        }
    }, [props.task?.immunisationTypeTasks,
        props.questions,
        loadingImmunisationTypes,
        loadingConsultation,
        setImmunisationTypeTasks,
        setLoadingImmunisationTypes,
        setLoadingConsultation]);

    const onConsentChanged = (newValue: string) => {
        setConsentToGiveVaccine(!!newValue);
        immunisationTypeTasks.map(immunisationTypeTask => {
            questionsList.map(q => {
                updateDependentQuestion(
                    `VaccinationConsultationTask${immunisationTypeTask.id}`,
                    { ...q },
                    newValue as string);
            });
        });
    };

    const loadQuestionnaire = async () => {
        return props.loadQuestionnaire(props.ohCaseId, props.taskId, props.customerId);
    };

    const submitQuestionnaire = async (questions: AnsweredQuestionWithId[]): Promise<boolean> => {
        if (questions.some(q => !q.isValid)) {
            props.submitInvalidQuestionnaire();
            return false;
        }

        await props.submitQuestionnaire(questions, immunisationTypeTasks.map(t => t.id));

        return true;
    };

    const updateDependentQuestion = (sectionId: string, question: { id: string; questionText: string }, answer: string) => {
        if (answer) {
            props.updateAnswer({ sectionId, id: question.id, question: { id: question.id, question: question.questionText, answer: "" } });
        } else {
            props.removeQuestion({ sectionId, id: question.id });
        }
    };

    const getQuestionWithId = (id: string) => {
        return props.questions.find(q => q.id === id);
    };

    const getAnswerToQuestionWithId = (id: string) => {
        const question = props.questions.find(q => q.id === id);
        return question?.answer;
    };

    const consentGivenQuestion = getQuestionWithId(consentGivenQuestionId);
    const consentGivenQuestionWasFilledInVaccinationConsentSection =
        consentGivenQuestion !== undefined
        && consentGivenQuestion.sectionId === "VaccinationConsent";
    const consentGivenQuestionWasFilledInVaccinationsSection =
        consentGivenQuestion !== undefined
        && consentGivenQuestion.sectionId === "Vaccinations";

    const noVaccinesAdministeredTodayQuestion = getQuestionWithId(noVaccinesAdministeredTodayQuestionId);
    const noVaccinesAdministeredTodayQuestionWasFilledInVaccinationsSection =
        noVaccinesAdministeredTodayQuestion !== undefined
        && noVaccinesAdministeredTodayQuestion.sectionId === "Vaccinations";

    const pleaseExplainWhyNoVaccinationsWereGivenQuestionHasAnswer = getAnswerToQuestionWithId(pleaseExplainWhyNoVaccinationsWereGivenId) !== undefined;

    return (
        <Container>
            <h2>
                Vaccination Consultation for
                {props.ohCase && ` ${props.ohCase.employeeFirstName || ""} ${props.ohCase.employeeLastName || ""}`}
            </h2>
            {!props.readonly && <Container textAlign="right" className="dna-button-container">
                <DidNotAttendButton
                    customerId={props.customerId}
                    ohCaseId={props.ohCaseId}
                    task={props.task}
                />
            </Container>}
            <OldProgressBarQuestionnaireContainer
                loadQuestionnaire={loadQuestionnaire}
                questionnaireKey={QUESTIONNAIRE_KEY}
                readonly={props.readonly}
                submitQuestionnaire={submitQuestionnaire}
                otherActionButtons={
                    <a
                        href={`/customers/${props.customerId}/employeeRecords/${props.ohCase.employeeRecordId}/immunisations`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Button content="View Imms Record" type="button" />
                    </a>
                }
                questionnaireType={QuestionnaireTypes.Other}
            >
                <ConfidentialitySectionPrint
                    index={0}
                    ohCase={props.ohCase}
                    employeeRecord={props.employeeRecord}
                    questionnaireKey={QUESTIONNAIRE_KEY}
                    readonly={props.readonly}
                />
                <DataProtectionSectionPrint
                    index={1}
                    questionnaireKey={QUESTIONNAIRE_KEY}
                    readonly={props.readonly}
                />
                {
                    !noVaccinesAdministeredToday &&
                    consentGivenQuestionWasFilledInVaccinationConsentSection &&
                    <OldProgressBarQuestionnaireSection id="VaccinationConsent" title="Vaccination Consent">
                        <CheckboxQuestion
                            readonly={props.readonly}
                            questionConfig={({ id: consentGivenQuestionId, questionText: "Consent to give vaccine(s) confirmed" })}
                            value={consentGivenQuestionId}
                            onAnswerChange={onConsentChanged}
                        />
                    </OldProgressBarQuestionnaireSection>
                }
                <OldProgressBarQuestionnaireSection id="Vaccinations" title="Vaccinations">
                    <CheckboxQuestion
                        readonly={props.readonly}
                        questionConfig={({ id: "GoodHealthAtPresent", questionText: "Are you in good health at present?" })}
                        value="GoodHealthAtPresent"
                    />
                    <CheckboxQuestion
                        readonly={props.readonly}
                        questionConfig={({
                            id: "SufferingFromAnyIllnessLoweredYourImmunity",
                            questionText: "Are you suffering from any illness, which has lowered your immunity?"
                        })}
                        value="SufferingFromAnyIllnessLoweredYourImmunity"
                    />
                    <CheckboxQuestion
                        readonly={props.readonly}
                        questionConfig={({
                            id: "ReceivingAnyMedicalTreatmentLowersImmunity",
                            // eslint-disable-next-line max-len
                            questionText: "Have you in the last 3 months had or are you receiving any medical treatment that lowers immunity such as chemotherapy, radiotherapy or steroids. E.g. azathioprine, cyclosporin, methotrexate, cyclophosphamide, leflunomide?"
                        })}
                        value="ReceivingAnyMedicalTreatmentLowersImmunity"
                    />
                    <CheckboxQuestion
                        readonly={props.readonly}
                        questionConfig={({
                            id: "TakingAnyMedication",
                            questionText: "Are you taking any medication? If yes, please list in comments below"
                        })}
                        value="TakingAnyMedication"
                    />
                    <CheckboxQuestion
                        readonly={props.readonly}
                        questionConfig={({
                            id: "AllergicToAnything",
                            questionText: "Are you allergic to anything? E.g. latex, yeast, eggs, gelatine or antibiotics?"
                        })}
                        value="AllergicToAnything"
                    />
                    <CheckboxQuestion
                        readonly={props.readonly}
                        questionConfig={({ id: "HadSevereReactionVaccine", questionText: "Have you ever had a severe reaction to a vaccine?" })}
                        value="HadSevereReactionVaccine"
                    />
                    <CheckboxQuestion
                        readonly={props.readonly}
                        questionConfig={({ id: "AreYouPregnant", questionText: "Are you or do you think you are pregnant or are you breast feeding?" })}
                        value="AreYouPregnant"
                    />
                    <CheckboxQuestion
                        readonly={props.readonly}
                        questionConfig={({
                            id: "HadAnyOtherVaccine",
                            questionText: "Have you had any other vaccine in the last month or are you planning any?"
                        })}
                        value="HadAnyOtherVaccine"
                    />
                    <CheckboxQuestion
                        readonly={props.readonly}
                        questionConfig={({
                            id: "HadBloodTransfusion",
                            questionText: "Have you had a blood transfusion or human immunoglobulin within the last 3 months?"
                        })}
                        value="HadBloodTransfusion"
                    />
                    {
                        consentGivenQuestionWasFilledInVaccinationsSection &&
                        <CheckboxQuestion
                            readonly={props.readonly}
                            questionConfig={({ id: consentGivenQuestionId, questionText: "Consent to give vaccine confirmed" })}
                            value={consentGivenQuestionId}
                            onAnswerChange={onConsentChanged}
                        />
                    }
                    <TextQuestion
                        inline
                        readonly={props.readonly}
                        multiline
                        questionConfig={({
                            id: "AdditionalCommentsOnAboveContraindications",
                            questionText: "Additional Comments on above contraindications"
                        })}
                        extraContent={!props.readonly ?
                            <TextSnippetSelector
                                onSelect={selectedSnippet =>
                                    props.updateAnswer({
                                        sectionId: "Vaccinations",
                                        id: "AdditionalCommentsOnAboveContraindications",
                                        question: {
                                            question: "Additional Comments on above contraindications",
                                            answer: selectedSnippet
                                        }
                                    })}
                            />
                            : null
                        }
                    />
                    {
                        noVaccinesAdministeredTodayQuestionWasFilledInVaccinationsSection &&
                        <CheckboxQuestion
                            readonly={props.readonly}
                            questionConfig={({
                                id: noVaccinesAdministeredTodayQuestionId,
                                questionText: "Tick here if no vaccines were given during this consultation or if ALL vaccines were refused"
                            })}
                            value={noVaccinesAdministeredTodayQuestionId}
                        />
                    }
                    {
                        pleaseExplainWhyNoVaccinationsWereGivenQuestionHasAnswer &&
                        <TextQuestion
                            inline
                            readonly={props.readonly}
                            multiline
                            questionConfig={({
                                id: pleaseExplainWhyNoVaccinationsWereGivenId,
                                questionText: "Please explain why no vaccinations were given"
                            })}
                            mandatory={true}
                        />
                    }
                </OldProgressBarQuestionnaireSection>
                {consentToGiveVaccine
                    && immunisationTypeTasks
                    && immunisationTypeTasks.map(immunisationTypeTask => {
                        if (!immunisationTypeTask) {
                            return null;
                        }
                        return (
                            <OldProgressBarQuestionnaireSection
                                id={`VaccinationConsultationTask${immunisationTypeTask.id}`}
                                title={immunisationTypeTask.name}
                                key={immunisationTypeTask.id}
                            >
                                <DropdownQuestion
                                    readonly={props.readonly}
                                    questionConfig={manufacturerAndDoseQuestion}
                                    options={vaccines}
                                    mandatory
                                />
                                <TextQuestion
                                    inline
                                    readonly={props.readonly}
                                    multiline
                                    questionConfig={batchNumberQuestion}
                                    validators={[createValidator<string>(value => value.length <= 100, "Batch number must be 100 characters or less")]}
                                />
                                <OldDateQuestion
                                    inline
                                    readonly={props.readonly}
                                    questionConfig={expiryDateQuestion}
                                    validators={[createValidator(value => moment().isSameOrBefore(value, "day"), "Expiry date must be in the future")]}
                                />
                                <DropdownQuestion
                                    readonly={props.readonly}
                                    questionConfig={routeQuestion}
                                    options={vaccineRoute}
                                    mandatory
                                />
                                <DropdownQuestion
                                    readonly={props.readonly}
                                    questionConfig={siteQuestion}
                                    options={vaccineSite}
                                    mandatory
                                />
                                <CheckboxQuestion
                                    readonly={props.readonly}
                                    questionConfig={adviceLeafletProvidedQuestion}
                                    value="AdviceLeafletProvided"
                                />
                            </OldProgressBarQuestionnaireSection>
                        );
                    }
                    )}
                {!props.readonly && props.ohCase &&
                    <OldProgressBarQuestionnaireSection
                        id="NextTasks"
                        title="Next Tasks"
                        neverHide={true}
                    >
                        {props.tasksSection}
                    </OldProgressBarQuestionnaireSection>
                }

            </OldProgressBarQuestionnaireContainer>
        </Container>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => {
    const ohCase = ohCaseSelector(state);
    const taskId = state.router.params!.taskId;
    const task = (ohCase.tasks && ohCase.tasks.find(t => t.id === taskId)) as ImmunisationTaskModel;
    const questionnaireState = state.questionnaires[QUESTIONNAIRE_KEY];
    return {
        customerId: customerIdSelector(state),
        ohCaseId: caseIdSelector(state),
        ohCase,
        employeeRecord: employeeRecordSelector(state),
        questions: questionnaireState ? uniq(flatten(questionnaireState.sections.map(section => values(section.questions)))) : [],
        taskId,
        task,
        readonly: task && task.completed
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>): DispatchProps => ({
    loadOhCaseDetail: (ohCaseId: string, customerId: string) => dispatch(loadOhCaseDetail({ ohCaseId, customerId })),
    loadQuestionnaire: (ohCaseId: string, taskId: string) => {
        dispatch(loadVaccinationConsultation(ohCaseId, taskId, QUESTIONNAIRE_KEY));
    },
    submitInvalidQuestionnaire: () => dispatch(submitInvalidQuestionnaire(QUESTIONNAIRE_KEY, true)),
    updateAnswer: payload => dispatch(updateQuestion({ ...payload, questionnaireKey: QUESTIONNAIRE_KEY })),
    removeQuestion: payload => dispatch(removeQuestion({ ...payload, questionnaireKey: QUESTIONNAIRE_KEY }))
});

export const VaccinationConsultationNotesPrint = connect(mapStateToProps, mapDispatchToProps)(VaccinationConsultationNotesPrintUnconnected);
